import React, { useState, useEffect } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  Steps,
  Row,
  Col,
  Card,
  Button,
  Space,
  message,
  Switch,
  InputNumber,
  Divider,
  Typography,
} from 'antd';
import {
  UserOutlined,
  ContactsOutlined,
  SettingOutlined,
  CheckCircleOutlined,
} from '@ant-design/icons';
import { apiService, Client, Coach } from '../../../../services/api';

const { Option } = Select;
const { TextArea } = Input;
const { Title, Text } = Typography;

interface AddClientModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: (client: Client) => void;
}

interface ClientFormData {
  // User information
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string;
  
  // Client specific information
  tier: 'BASIC' | 'STANDARD' | 'PREMIUM' | 'ELITE';
  subscriptionType?: 'WEEKLY' | 'MONTHLY' | 'QUARTERLY' | 'YEARLY';
  agreedPrice?: number;
  assignedCoachId?: string;
  
  // Services
  services: {
    training: boolean;
    nutrition: boolean;
    habitCoaching: boolean;
  };
  
  // Goals and health
  goals: string[];
  healthConditions: string[];
  
  // Emergency contact
  emergencyContact?: {
    name: string;
    phone: string;
    relationship: string;
  };
  
  // Notes
  notes?: string;
}

const AddClientModal: React.FC<AddClientModalProps> = ({
  visible,
  onCancel,
  onSuccess,
}) => {
  const [form] = Form.useForm<ClientFormData>();
  const [currentStep, setCurrentStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [coaches, setCoaches] = useState<Coach[]>([]);
  const [formData, setFormData] = useState<Partial<ClientFormData>>({});
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);

  // Fetch coaches for assignment
  useEffect(() => {
    const fetchCoaches = async () => {
      try {
        const response = await apiService.getCoaches({ isActive: true });
        if (response.success && response.data) {
          setCoaches(response.data.data || []);
        }
      } catch (error) {
        console.error('Failed to fetch coaches:', error);
      }
    };

    if (visible) {
      fetchCoaches();
    }
  }, [visible]);

  // Populate form fields when step changes or modal opens
  useEffect(() => {
    if (visible) {
      if (Object.keys(formData).length > 0) {
        form.setFieldsValue(formData);
      } else {
        // Ensure services object is always initialized
        form.setFieldsValue({
          services: {
            training: false,
            nutrition: false,
            habitCoaching: false,
          },
        });
      }
    } else {
      // Reset confirmation modal state when main modal is closed
      setShowCancelConfirm(false);
    }
  }, [visible, currentStep, formData, form]);

  // Helper function to get complete form values including switches
  const getCompleteFormValues = () => {
    const rawValues = form.getFieldsValue(true);
    
    // Manually capture services switches (more reliable than getFieldsValue for switches)
    const servicesValues = {
      training: form.getFieldValue(['services', 'training']) || false,
      nutrition: form.getFieldValue(['services', 'nutrition']) || false,
      habitCoaching: form.getFieldValue(['services', 'habitCoaching']) || false,
    };
    
    const values = {
      ...rawValues,
      services: servicesValues,
    };
    
    console.log('getCompleteFormValues - raw form values:', rawValues);
    console.log('getCompleteFormValues - raw services from form:', rawValues.services);
    console.log('getCompleteFormValues - manual services:', servicesValues);
    console.log('getCompleteFormValues - final values:', values);
    
    return values;
  };

  const steps = [
    {
      title: 'Personal Info',
      icon: <UserOutlined />,
      description: 'Basic information',
    },
    {
      title: 'Services & Tier',
      icon: <SettingOutlined />,
      description: 'Service configuration',
    },
    {
      title: 'Contact & Goals',
      icon: <ContactsOutlined />,
      description: 'Additional details',
    },
    {
      title: 'Review',
      icon: <CheckCircleOutlined />,
      description: 'Confirm details',
    },
  ];

  const handleNext = () => {
    // Validate fields based on current step
    let fieldsToValidate: string[] = [];
    
    switch (currentStep) {
      case 0: // Personal Info
        fieldsToValidate = ['firstName', 'lastName', 'email'];
        break;
      case 1: // Services & Tier
        fieldsToValidate = ['tier'];
        break;
      case 2: // Contact & Goals - all optional
        fieldsToValidate = [];
        break;
      default:
        fieldsToValidate = [];
    }

    const validatePromise = fieldsToValidate.length > 0 
      ? form.validateFields(fieldsToValidate)
      : Promise.resolve();

    validatePromise.then(() => {
      // Save current form values to state - explicitly capture all data
      const rawValues = form.getFieldsValue(true); // Get all field values including unchecked switches
      
      // Manually capture services switches (Ant Design issue with getFieldsValue)
      const servicesValues = {
        training: form.getFieldValue(['services', 'training']) || false,
        nutrition: form.getFieldValue(['services', 'nutrition']) || false,
        habitCoaching: form.getFieldValue(['services', 'habitCoaching']) || false,
      };
      
      // Combine all values
      const currentValues = {
        ...rawValues,
        services: servicesValues,
      };
      
      console.log('=== STEP NAVIGATION DEBUG ===');
      console.log('Raw form getFieldsValue(true):', rawValues);
      console.log('Manually captured services:', servicesValues);
      console.log('Combined current values:', currentValues);
      console.log('Emergency contact in current values:', currentValues.emergencyContact);
      console.log('Phone number in current values:', currentValues.phoneNumber);
      
      setFormData(prev => {
        const updated = { ...prev, ...currentValues };
        console.log('Updated formData:', updated);
        return updated;
      });
      setCurrentStep(current => current + 1);
    }).catch((errorInfo) => {
      console.log('Form validation failed:', errorInfo);
      message.error('Please fill in all required fields for this step');
    });
  };

  const handlePrev = () => {
    // Save current form values before going back - explicitly capture all data
    const rawValues = form.getFieldsValue(true);
    
    // Manually capture services switches
    const servicesValues = {
      training: form.getFieldValue(['services', 'training']) || false,
      nutrition: form.getFieldValue(['services', 'nutrition']) || false,
      habitCoaching: form.getFieldValue(['services', 'habitCoaching']) || false,
    };
    
    const currentValues = {
      ...rawValues,
      services: servicesValues,
    };
    
    console.log('Going back - raw values:', rawValues);
    console.log('Going back - services:', servicesValues);
    console.log('Going back - combined values:', currentValues);
    
    setFormData(prev => {
      const updated = { ...prev, ...currentValues };
      console.log('Going back - updated formData:', updated);
      return updated;
    });
    setCurrentStep(current => current - 1);
  };

  const handleSubmit = async () => {
    try {
      // Get current form values and combine with saved data
      await form.validateFields();
      const currentValues = getCompleteFormValues();
      const values = { ...formData, ...currentValues };
      setLoading(true);

      console.log('AddClient - Raw combined values:', values);
      console.log('AddClient - FormData state:', formData);
      console.log('AddClient - Current form values:', currentValues);

      // Ensure services object is complete
      const services = {
        training: !!values.services?.training,
        nutrition: !!values.services?.nutrition,
        habitCoaching: !!values.services?.habitCoaching,
      };

      console.log('AddClient - Processed services:', services);
      console.log('AddClient - Services validation check:');
      console.log('  - services object exists:', !!values.services);
      console.log('  - training:', values.services?.training);
      console.log('  - nutrition:', values.services?.nutrition);
      console.log('  - habitCoaching:', values.services?.habitCoaching);
      
      // Debug emergency contact
      console.log('AddClient - Emergency contact raw:', values.emergencyContact);
      console.log('AddClient - Phone number:', values.phoneNumber);

      // Prepare client data for API (backend expects flat structure)
      // phoneNumber is included as it's part of user creation within client creation
      const clientData = {
        firstName: values.firstName,
        lastName: values.lastName,
        email: values.email,
        phoneNumber: values.phoneNumber || undefined,
        tier: values.tier,
        subscriptionType: values.subscriptionType || undefined,
        agreedPrice: values.agreedPrice ? Number(values.agreedPrice) : undefined,
        assignedCoachId: values.assignedCoachId || undefined,
        services: services,
        goals: values.goals || [],
        healthConditions: values.healthConditions || [],
        emergencyContact: (values.emergencyContact?.name || values.emergencyContact?.phone || values.emergencyContact?.relationship) 
          ? {
              name: values.emergencyContact?.name || '',
              phone: values.emergencyContact?.phone || '',
              relationship: values.emergencyContact?.relationship || '',
            }
          : undefined,
        notes: values.notes || undefined,
      };

      console.log('AddClient - Sending client data to API:', clientData);

      const response = await apiService.createClient(clientData);
      
      if (response.success && response.data) {
        message.success('Client created successfully!');
        onSuccess(response.data);
        handleReset();
      } else {
        throw new Error(response.error || 'Failed to create client');
      }
    } catch (error: any) {
      console.error('Failed to create client:', error);
      
      // Handle API validation errors
      if (error.response?.data?.details) {
        const validationErrors = error.response.data.details;
        
        // Set form field errors
        const fieldErrors = validationErrors.map((err: any) => ({
          name: err.field.split('.'), // Handle nested fields like 'emergencyContact.phone'
          errors: [err.message],
        }));
        
        form.setFields(fieldErrors);
        
        // Navigate to the step with the error
        const errorField = validationErrors[0]?.field;
        if (errorField) {
          if (errorField.includes('firstName') || errorField.includes('lastName') || errorField.includes('email') || errorField.includes('phoneNumber')) {
            setCurrentStep(0); // Personal Info
          } else if (errorField.includes('tier') || errorField.includes('subscription') || errorField.includes('services')) {
            setCurrentStep(1); // Services & Tier
          } else if (errorField.includes('emergencyContact') || errorField.includes('goals') || errorField.includes('healthConditions') || errorField.includes('notes')) {
            setCurrentStep(2); // Contact & Goals
          }
        }
        
        // Show error message with details
        const errorMessages = validationErrors.map((err: any) => `${err.field}: ${err.message}`).join(', ');
        message.error(`Validation failed: ${errorMessages}`);
      } else if (error.response?.status === 409) {
        // Handle conflict errors (user already exists as client)
        message.error('This email is already registered as a client. Please use a different email address.');
        setCurrentStep(0); // Go back to personal info step
      } else if (error.response?.status === 400) {
        // Handle bad request errors
        const errorMsg = error.response?.data?.error?.message || error.response?.data?.message || 'Invalid request data. Please check all fields and try again.';
        message.error(errorMsg);
      } else if (error.response?.status === 500) {
        // Handle server errors
        message.error('Server error occurred. Please try again in a few moments or contact support if the problem persists.');
      } else {
        message.error(error.message || 'Failed to create client. Please check all fields and try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Check if form has any data entered
  const hasFormData = () => {
    try {
      // Get current form values (what's currently in the form)
      const currentValues = form.getFieldsValue();
      // Get saved form data from state
      const savedValues = formData;
      
      const hasPersonalInfo = currentValues.firstName || currentValues.lastName || currentValues.email || currentValues.phoneNumber;
      const hasFormDataState = Object.keys(formData).length > 0;
      
      console.log('hasFormData check - simple:', {
        hasPersonalInfo,
        hasFormDataState,
        currentStep
      });
      
      return hasPersonalInfo || hasFormDataState;
    } catch (error) {
      console.error('Error in hasFormData:', error);
      return false; // If error, just allow cancel
    }
  };

  const handleCancel = () => {
    try {
      const hasData = hasFormData();
      console.log('🚫 Cancel clicked - hasFormData:', hasData);
      
      if (hasData) {
        console.log('📋 Form has data, showing confirmation dialog...');
        setShowCancelConfirm(true);
      } else {
        console.log('📋 No form data, resetting directly...');
        handleReset();
      }
    } catch (error) {
      console.error('Error in handleCancel:', error);
      // If there's an error, just close the modal
      handleReset();
    }
  };

  const handleReset = () => {
    console.log('🔄 Resetting form and closing modal...');
    form.resetFields();
    setFormData({});
    setCurrentStep(0);
    setShowCancelConfirm(false); // Close confirmation modal
    onCancel();
    console.log('✅ Modal should be closed now');
  };

  const renderPersonalInfoStep = () => (
    <Card title="Personal Information" size="small">
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Form.Item
            name="firstName"
            label="First Name"
            rules={[{ required: true, message: 'Please enter first name' }]}
          >
            <Input placeholder="Enter first name" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item
            name="lastName"
            label="Last Name"
            rules={[{ required: true, message: 'Please enter last name' }]}
          >
            <Input placeholder="Enter last name" />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item
            name="email"
            label="Email"
            rules={[
              { required: true, message: 'Please enter email' },
              { type: 'email', message: 'Please enter a valid email' },
            ]}
          >
            <Input placeholder="Enter email address" />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item 
            name="phoneNumber" 
            label="Phone Number"
            rules={[
              {
                pattern: /^\+?[1-9]\d{1,14}$/,
                message: 'Enter a valid phone number (e.g., +14375051636 or 14375051636). Must start with 1-9.',
              },
            ]}
            extra="Format: +14375051636 or 14375051636 (must start with 1-9, not 0)"
          >
            <Input placeholder="Enter phone number (e.g., +14375051636)" />
          </Form.Item>
        </Col>
      </Row>
    </Card>
  );

  const renderServicesStep = () => (
    <Card title="Services & Subscription" size="small">
      <Row gutter={[16, 16]}>
        <Col span={12}>
          <Form.Item
            name="tier"
            label="Tier"
            rules={[{ required: true, message: 'Please select a tier' }]}
          >
            <Select placeholder="Select tier">
              <Option value="BASIC">Basic</Option>
              <Option value="STANDARD">Standard</Option>
              <Option value="PREMIUM">Premium</Option>
              <Option value="ELITE">Elite</Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="subscriptionType" label="Subscription Type">
            <Select placeholder="Select subscription type" allowClear>
              <Option value="WEEKLY">Weekly</Option>
              <Option value="MONTHLY">Monthly</Option>
              <Option value="QUARTERLY">Quarterly</Option>
              <Option value="YEARLY">Yearly</Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="agreedPrice" label="Agreed Price ($)">
            <InputNumber
              style={{ width: '100%' }}
              placeholder="Enter agreed price"
              min={0}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="assignedCoachId" label="Assigned Coach">
            <Select placeholder="Select a coach" allowClear>
              {coaches.map(coach => (
                <Option key={coach.id} value={coach.id}>
                  {coach.user ? `${coach.user.firstName} ${coach.user.lastName}` : 'Unknown Coach'}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
        <Col span={24}>
          <Divider>Services</Divider>
          <Row gutter={[16, 16]}>
            <Col span={8}>
              <Form.Item name={['services', 'training']} valuePropName="checked">
                <Space>
                  <Switch 
                    checked={form.getFieldValue(['services', 'training'])}
                    onChange={(checked) => {
                      console.log('🏋️ Training switch toggled:', checked);
                      // Force form field update
                      form.setFieldValue(['services', 'training'], checked);
                      console.log('🏋️ Form training value after update:', form.getFieldValue(['services', 'training']));
                    }} 
                  />
                  <Text>Training</Text>
                </Space>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name={['services', 'nutrition']} valuePropName="checked">
                <Space>
                  <Switch 
                    checked={form.getFieldValue(['services', 'nutrition'])}
                    onChange={(checked) => {
                      console.log('🥗 Nutrition switch toggled:', checked);
                      // Force form field update
                      form.setFieldValue(['services', 'nutrition'], checked);
                      console.log('🥗 Form nutrition value after update:', form.getFieldValue(['services', 'nutrition']));
                    }} 
                  />
                  <Text>Nutrition</Text>
                </Space>
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item name={['services', 'habitCoaching']} valuePropName="checked">
                <Space>
                  <Switch 
                    checked={form.getFieldValue(['services', 'habitCoaching'])}
                    onChange={(checked) => {
                      console.log('🎯 Habit Coaching switch toggled:', checked);
                      // Force form field update
                      form.setFieldValue(['services', 'habitCoaching'], checked);
                      console.log('🎯 Form habit coaching value after update:', form.getFieldValue(['services', 'habitCoaching']));
                    }} 
                  />
                  <Text>Habit Coaching</Text>
                </Space>
              </Form.Item>
            </Col>
          </Row>
        </Col>
      </Row>
    </Card>
  );

  const renderContactStep = () => (
    <Space direction="vertical" style={{ width: '100%' }} size="middle">
      <Card title="Goals & Health" size="small">
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Form.Item name="goals" label="Goals">
              <Select mode="tags" placeholder="Enter goals (press Enter to add)">
                <Option value="weight_loss">Weight Loss</Option>
                <Option value="muscle_gain">Muscle Gain</Option>
                <Option value="strength">Strength Training</Option>
                <Option value="endurance">Endurance</Option>
                <Option value="flexibility">Flexibility</Option>
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item name="healthConditions" label="Health Conditions">
              <Select mode="tags" placeholder="Enter health conditions (press Enter to add)">
                <Option value="diabetes">Diabetes</Option>
                <Option value="hypertension">Hypertension</Option>
                <Option value="heart_disease">Heart Disease</Option>
                <Option value="arthritis">Arthritis</Option>
              </Select>
            </Form.Item>
          </Col>
        </Row>
      </Card>

      <Card title="Emergency Contact" size="small">
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Form.Item 
              name={['emergencyContact', 'name']} 
              label="Contact Name"
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const phone = getFieldValue(['emergencyContact', 'phone']);
                    const relationship = getFieldValue(['emergencyContact', 'relationship']);
                    
                    // If any emergency contact field is filled, name is required
                    if ((phone || relationship) && !value) {
                      return Promise.reject(new Error('Contact name is required when providing emergency contact'));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <Input placeholder="Enter emergency contact name" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item 
              name={['emergencyContact', 'phone']} 
              label="Contact Phone"
              rules={[
                {
                  pattern: /^\+?[1-9]\d{1,14}$/,
                  message: 'Enter a valid phone number (e.g., +14375051636). Must start with 1-9.',
                },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const name = getFieldValue(['emergencyContact', 'name']);
                    const relationship = getFieldValue(['emergencyContact', 'relationship']);
                    
                    // If any emergency contact field is filled, phone is required
                    if ((name || relationship) && !value) {
                      return Promise.reject(new Error('Contact phone is required when providing emergency contact'));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
              extra="Format: +14375051636 or 14375051636 (must start with 1-9, not 0)"
            >
              <Input placeholder="Enter emergency contact phone (e.g., +14375051636)" />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item 
              name={['emergencyContact', 'relationship']} 
              label="Relationship"
              rules={[
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    const name = getFieldValue(['emergencyContact', 'name']);
                    const phone = getFieldValue(['emergencyContact', 'phone']);
                    
                    // If any emergency contact field is filled, relationship is required
                    if ((name || phone) && !value) {
                      return Promise.reject(new Error('Relationship is required when providing emergency contact'));
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <Input placeholder="e.g., Spouse, Parent, Friend" />
            </Form.Item>
          </Col>
        </Row>
      </Card>

      <Card title="Additional Notes" size="small">
        <Form.Item name="notes" label="Notes">
          <TextArea
            rows={4}
            placeholder="Any additional notes about the client..."
          />
        </Form.Item>
      </Card>
    </Space>
  );

  const renderReviewStep = () => {
    // Use saved form data and current form values combined
    const currentValues = getCompleteFormValues();
    const values = { ...formData, ...currentValues };
    
    // Debug: Log the values being retrieved
    console.log('Review step - combined values:', values);
    console.log('Review step - formData state:', formData);
    console.log('Review step - current form values:', currentValues);
    
    // Format services for display
    const selectedServices = values.services ? 
      Object.entries(values.services)
        .filter(([, enabled]) => enabled)
        .map(([service]) => {
          // Capitalize first letter and add spaces
          return service.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
        })
        .join(', ') : 'None selected';

    // Format assigned coach name
    const assignedCoach = values.assignedCoachId ? 
      coaches.find(coach => coach.id === values.assignedCoachId) : null;
    const coachName = assignedCoach?.user ? 
      `${assignedCoach.user.firstName} ${assignedCoach.user.lastName}` : 'Not assigned';

    return (
      <Card title="Review Information" size="small">
        <Row gutter={[16, 16]}>
          <Col span={12}>
            <Text strong>Name:</Text> {(values.firstName && values.lastName) ? 
              `${values.firstName} ${values.lastName}` : 'Not provided'}
          </Col>
          <Col span={12}>
            <Text strong>Email:</Text> {values.email || 'Not provided'}
          </Col>
          <Col span={12}>
            <Text strong>Phone:</Text> {values.phoneNumber || 'Not provided'}
          </Col>
          <Col span={12}>
            <Text strong>Tier:</Text> {values.tier || 'Not selected'}
          </Col>
          <Col span={12}>
            <Text strong>Subscription:</Text> {values.subscriptionType || 'Not set'}
          </Col>
          <Col span={12}>
            <Text strong>Agreed Price:</Text> {values.agreedPrice ? `$${values.agreedPrice}` : 'Not set'}
          </Col>
          <Col span={12}>
            <Text strong>Assigned Coach:</Text> {coachName}
          </Col>
          <Col span={12}>
            <Text strong>Services:</Text> {selectedServices}
          </Col>
          <Col span={24}>
            <Text strong>Goals:</Text> {values.goals?.length > 0 ? values.goals.join(', ') : 'None specified'}
          </Col>
          <Col span={24}>
            <Text strong>Health Conditions:</Text> {values.healthConditions?.length > 0 ? values.healthConditions.join(', ') : 'None specified'}
          </Col>
          {values.emergencyContact?.name && (
            <Col span={24}>
              <Text strong>Emergency Contact:</Text> {values.emergencyContact.name} 
              {values.emergencyContact.phone && ` (${values.emergencyContact.phone})`}
              {values.emergencyContact.relationship && ` - ${values.emergencyContact.relationship}`}
            </Col>
          )}
          {values.notes && (
            <Col span={24}>
              <Text strong>Notes:</Text> {values.notes}
            </Col>
          )}
        </Row>
      </Card>
    );
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 0:
        return renderPersonalInfoStep();
      case 1:
        return renderServicesStep();
      case 2:
        return renderContactStep();
      case 3:
        return renderReviewStep();
      default:
        return renderPersonalInfoStep();
    }
  };

  return (
    <Modal
      title="Add New Client"
      open={visible}
      onCancel={handleCancel}
      width={800}
      footer={null}
      destroyOnClose
    >
      <Steps current={currentStep} items={steps} style={{ marginBottom: 24 }} />
      
      <Form
        form={form}
        layout="vertical"
        initialValues={{
          services: {
            training: false,
            nutrition: false,
            habitCoaching: false,
          },
        }}
        onFinish={handleSubmit}
        onValuesChange={(changedValues, allValues) => {
          // Debug services changes specifically
          if (changedValues.services) {
            console.log('🔄 Services form values changed:', changedValues.services);
            console.log('🔄 All services in form:', allValues.services);
          }
          
          // Force form to trigger re-render when services change
          if (changedValues.services) {
            setTimeout(() => {
              const currentServices = form.getFieldValue('services') || {};
              console.log('🔄 Services after timeout:', currentServices);
            }, 50);
          }
        }}
      >
        {renderStepContent()}
      </Form>

      <Divider />

      <Row justify="space-between">
        <Col>
          {currentStep > 0 && (
            <Button onClick={handlePrev}>
              Previous
            </Button>
          )}
        </Col>
        <Col>
          <Space>
            <Button onClick={handleCancel}>
              Cancel
            </Button>
            {currentStep < steps.length - 1 ? (
              <Button type="primary" onClick={handleNext}>
                Next
              </Button>
            ) : (
              <Button type="primary" loading={loading} onClick={handleSubmit}>
                Create Client
              </Button>
            )}
          </Space>
        </Col>
      </Row>

      <Modal
        title="Confirm Discard"
        open={showCancelConfirm}
        onCancel={() => setShowCancelConfirm(false)}
        onOk={() => {
          handleReset();
          setShowCancelConfirm(false);
        }}
        okText="Yes, discard data"
        cancelText="No, keep editing"
        okType="danger"
      >
        <p>All entered data will be lost. Do you want to continue?</p>
      </Modal>
    </Modal>
  );
};

export default AddClientModal; 